import React, { useEffect, useState } from "react";
import { PlayerAvatar } from "../../styled-components/PlayerCard.Styles";
import { IPlayerCardData } from "../../types/IPlayerDetail";
import ChipComponent from "../../ui/Chip";
import LabelText from "../../ui/LabelText";
import CustomizedTooltips from "../../ui/Tooltip";
import Badge from "@mui/material/Badge";
import EuroIcon from "@mui/icons-material/Euro";
import {
  PlayerSmallData,
  PlayerSmallDataText,
  PlayerClubCountryInfoData,
  PlayerClubInfoData,
  PlayerLargeDataText,
  PlayerLargeDataValue,
  useStyles,
} from "../../styled-components/PlayerCard.Styles";
import { styled } from "styled-components";
import { BASE_URL } from "../../config/apiUrls";
import noImagePlayer from "../../assets/icons/noImagePlayer.svg";
import noImageClub from "../../assets/icons/noImageClub.svg";
import InfoIcon from "../../assets/icons/infoicon.svg";
import { ArchetypeInfoDrawer } from "../../pages/Chemistry/UIContent";
import { useSearch } from "../../contexts/PlayerSearchContext";
import EmptyDataButton from "../../ui/EmptyDataButton";

interface PlayerOverviewProps {
  playerData?: IPlayerCardData;
}

const PlayerCardData = styled.div`
  background: ${({ theme }) => theme.playerCardBackground};
  border-bottom: 1px solid ${({ theme }) => theme.playerDataBorder};
`;

const PlayerOverview: React.FC<PlayerOverviewProps> = ({ playerData }) => {

  const {playerPrimaryPosition , setPlayerPrimaryPosition ,setSelectedAttr , setAttributesDescription} = useSearch()
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(false);
  const classes = useStyles();
  const nationalityTooltip = playerData?.eligibleForMultiNationality ? (
    <div>
      <LabelText className="tooltip-text">Eligible to play for {playerData?.multiNationalityCountries.length} countries:</LabelText>
      <ul style={{ margin: "0px", paddingLeft: "20px" }}>
        {playerData?.multiNationalityCountries.map((country) => (
          <li key={country}>
            <LabelText className="tooltip-text">{country}</LabelText>
          </li>
        ))}
      </ul>
    </div>
  ) : null;
  const handleClick = () => {
    if (playerData?.clubName !== null) {
      window.location.href = `${BASE_URL}/club/${playerData?.clubId}`;
    }
  };

  useEffect(()=>{
        setPlayerPrimaryPosition(playerData?.primaryPlayingPositionCode!)
        if (
          ['lb', 'lwb', 'rwb', 'rw', 'lcb3', 'lcb', 'cb', 'rcb', 'rcb3'].includes(playerData?.primaryPlayingPositionCode!)
        ) {
          setSelectedAttr('Tackling');
          setAttributesDescription('Each dot represents a tackling attempt.')
        } else if (
          ['ldmf', 'dmf', 'rdmf', 'lcmf', 'rcmf', 'lw', 'rb',].includes(playerData?.primaryPlayingPositionCode!)
        ) {
          setSelectedAttr('Creativity');
          setAttributesDescription('The heat is made from a player’s creative actions. ')
        } else if (
          ['lamf', 'amf', 'ramf', 'lwf', 'rwf', 'st'].includes(playerData?.primaryPlayingPositionCode!)
        ) {
          setSelectedAttr('Finishing - Shots');
        }
        else{
          setSelectedAttr('Finishing - Shots');
        }
  },[playerData])

  return (
    <div className="player-card-data-main">
      <PlayerCardData className="player-card-data">
        <div className="player-card-detail">
          <div className="player-header">
            <div className="player-chip">
              {playerData?.isInjured && <ChipComponent status={"INJURED"} />}
              {playerData?.onLoan && <ChipComponent status={"ON LOAN"} />}
              {playerData?.givenOnLoan && <ChipComponent status={"LOANED OUT"} />}
              {playerData?.isRetired && <ChipComponent status={"RETIRED"} />}
            </div>
            <PlayerAvatar
              className="player-avatar"
              backgroundImage={playerData?.playerImageURL ? playerData?.playerImageURL : noImagePlayer}
            />
          </div>
          <div className="player-name-header">
            {playerData?.playerLabel?.split(" ")?.length! > 2 ? (
            <>
              <LabelText className="three-named-player">{playerData?.playerLabel}</LabelText>
            </>
            ) : playerData?.playerLabel?.split(" ")?.length === 1 ? (
            <LabelText className="player-detail-family-name">{playerData?.playerLabel}</LabelText>
            ) : (
            <>
              <div className="player-name-squad">
                <LabelText className={playerData?.isEasternPlayer ? "player-detail-family-name" : "player-detail-given-name"}>
                  {playerData?.isEasternPlayer ? playerData?.playerLabel?.split(" ")[1] : playerData?.playerLabel?.split(" ")[0]}
                </LabelText>
              </div>
              <LabelText className={playerData?.isEasternPlayer ? "player-detail-given-name" : "player-detail-family-name"}>
                {playerData?.isEasternPlayer ? playerData?.playerLabel?.split(" ")[0] : playerData?.playerLabel?.split(" ")[1]}
              </LabelText>
            </>
            )}
            <div className="player-details">
              {playerData?.positionTag?.map((tag, index) => (
                <PlayerSmallData key={index}>
                  <PlayerSmallDataText className="descriptive-text">{tag}</PlayerSmallDataText>
                </PlayerSmallData>
              ))}
              {playerData?.archetypes && playerData.archetypes.length > 0 && (
                <PlayerSmallData key={playerData?.archetypes[0]?.id}>
                  <PlayerSmallDataText className="descriptive-text">{playerData?.archetypes[0]?.archetypeName}</PlayerSmallDataText>
                  {/* <img src={InfoIcon} alt="archetpe-info" className="archetype-info-icon" onClick={() => setSidebarOpen(true)} /> */}
                </PlayerSmallData>
              )}
              {!playerData?.isGoalKeeper && (
                <PlayerSmallData>
                  <PlayerSmallDataText className="descriptive-text">{playerData?.footTag || ""}</PlayerSmallDataText>
                </PlayerSmallData>
              )}
              <PlayerSmallData>
                <PlayerSmallDataText className="descriptive-text">AGE: {playerData?.age || ""}</PlayerSmallDataText>
              </PlayerSmallData>
              <PlayerSmallData>
                <PlayerSmallDataText className="descriptive-text">HEIGHT: {playerData?.heightInCM || ""}cm</PlayerSmallDataText>
              </PlayerSmallData>
              {playerData?.isGoalKeeper && (
                <PlayerSmallData>
                  <PlayerSmallDataText className="descriptive-text">REACH: {playerData?.reach || ""}cm</PlayerSmallDataText>
                </PlayerSmallData>
              )}
            </div>
          </div>
        </div>
        <div className="player-clubInfo">
          {(playerData?.onLoan || playerData?.givenOnLoan || playerData?.loanClubId! > 0) && (
            <CustomizedTooltips title={`On loan from: ${playerData?.clubName}`}>
              <PlayerClubCountryInfoData
                className="on-loan club-data"
                onClick={() => {
                  window.location.href = `${BASE_URL}/club/${playerData?.clubId}`;
                }}
              >
                <img src={playerData?.clubImageURL ? playerData.clubImageURL : noImageClub} alt="ClubImageURL" />
              </PlayerClubCountryInfoData>
            </CustomizedTooltips>
          )}
          {(playerData?.onLoan || playerData?.givenOnLoan || playerData?.loanClubId! > 0) && (
            <PlayerClubCountryInfoData
              className="club-data"
              onClick={() => {
                window.location.href = `${BASE_URL}/club/${playerData?.loanClubId}`;
              }}
            >
              <PlayerClubInfoData country="country" className="player-descriptive-data">
                <PlayerLargeDataText className="descriptive-text">
                  {playerData?.givenOnLoan ? "LOANED OUT" : "ON LOAN"} - {playerData?.loanedTillMonthYear}
                </PlayerLargeDataText>
                <PlayerLargeDataValue className="descriptive-value">{playerData?.loanClubName || ""}</PlayerLargeDataValue>
              </PlayerClubInfoData>
              <img
                src={playerData?.loanClubImageURL ? playerData.loanClubImageURL : noImageClub}
                alt="loanClubImageURL"
                className="club-image"
              />
            </PlayerClubCountryInfoData>
          )}
          {!(playerData?.onLoan || playerData?.givenOnLoan || playerData?.loanClubId! > 0) && (
            <PlayerClubCountryInfoData className="club-data" onClick={handleClick}>
              <PlayerClubInfoData country="country" className="player-descriptive-data">
                <PlayerLargeDataText className="descriptive-text">CLUB</PlayerLargeDataText>
                {playerData?.clubName === null ? (
                  <PlayerLargeDataValue className="descriptive-value">No Current Club</PlayerLargeDataValue>
                ) : (
                  <PlayerLargeDataValue className="descriptive-value">{playerData?.clubName || ""}</PlayerLargeDataValue>
                )}
              </PlayerClubInfoData>
              <img src={playerData?.clubImageURL ? playerData.clubImageURL : noImageClub} alt="Club" className="club-image" />
            </PlayerClubCountryInfoData>
          )}
          <PlayerClubCountryInfoData displayborder={playerData?.nationalityCountryCode ? true : false}>
            <PlayerClubInfoData country="country" className="player-descriptive-data">
              <PlayerLargeDataText className="descriptive-text">NATIONALITY</PlayerLargeDataText>
              <PlayerLargeDataValue className="descriptive-value">
                {playerData?.nationalityCountry ? playerData.nationalityCountry : "Unknown"}
              </PlayerLargeDataValue>
            </PlayerClubInfoData>
            {playerData?.eligibleForMultiNationality ? (
              <CustomizedTooltips title={nationalityTooltip}>
                <Badge badgeContent={playerData?.multiNationalityCountries.length} showZero classes={{ badge: classes.badgeGreen }}>
                  {playerData.nationalityCountry !== null && (
                    <img
                      src={`/assets/country_flags/${playerData?.nationalityCountryCode}.svg`}
                      alt="Country Flag"
                      className="country-image"
                    />
                  )}
                </Badge>
              </CustomizedTooltips>
            ) : (
              playerData?.nationalityCountry !== null && (
                <img src={`/assets/country_flags/${playerData?.nationalityCountryCode}.svg`} alt="Country Flag" className="country-image" />
              )
            )}
          </PlayerClubCountryInfoData>
          <PlayerClubInfoData className="player-descriptive-data">
            <PlayerLargeDataText className="descriptive-text">market value</PlayerLargeDataText>
            <PlayerLargeDataValue className="descriptive-value transfer-data">
              <EuroIcon sx={{ fontSize: "20px" }} />
              {playerData?.transferValueText || ""}
            </PlayerLargeDataValue>
          </PlayerClubInfoData>
          <PlayerClubInfoData className="player-descriptive-data">
            <PlayerLargeDataText className="descriptive-text">true value</PlayerLargeDataText>
            <PlayerLargeDataValue className="descriptive-value transfer-data">
              {playerData?.trueValueText && (<EuroIcon sx={{ fontSize: "20px" }} />)}
              {playerData?.trueValueText || "--"}
            </PlayerLargeDataValue>
          </PlayerClubInfoData>
<PlayerClubInfoData className="player-descriptive-data">
  <PlayerLargeDataText className="descriptive-text">wages pw</PlayerLargeDataText>
  {(playerData?.wagesPW !== 0 && playerData?.wagesPW !== null) ? (
    <PlayerLargeDataValue className="descriptive-value transfer-data">
      <EuroIcon sx={{ fontSize: "20px" }} />
      {playerData?.wagesPWText}
    </PlayerLargeDataValue>
  ) : (
    // <PlayerLargeDataValue className="descriptive-value transfer-data">
      <EmptyDataButton label="Data not provided by club" />
    // </PlayerLargeDataValue> 
  )}
</PlayerClubInfoData>
          <PlayerClubInfoData className="player-descriptive-data">
            <PlayerLargeDataText className="descriptive-text">Starts / as Sub</PlayerLargeDataText>
            <PlayerLargeDataValue className="descriptive-value">
              {playerData?.starts || 0}/{playerData?.appearancesAsSub || 0}
            </PlayerLargeDataValue>
          </PlayerClubInfoData>
          <PlayerClubInfoData className="player-descriptive-data">
            <PlayerLargeDataText className="descriptive-text">Mins Played</PlayerLargeDataText>
            <PlayerLargeDataValue className="descriptive-value">{playerData?.minutesPlayed || 0}</PlayerLargeDataValue>
          </PlayerClubInfoData>
          <PlayerClubInfoData className="player-descriptive-data">
            <PlayerLargeDataText className="descriptive-text">Agent</PlayerLargeDataText>
            <PlayerLargeDataValue className="descriptive-value">
              {playerData?.playerAgent !== null && playerData?.playerAgent !== "" ? playerData?.playerAgent : "--"}
            </PlayerLargeDataValue>
          </PlayerClubInfoData>
        </div>
      </PlayerCardData>
      {playerData?.archetypes && playerData.archetypes.length > 0 && <ArchetypeInfoDrawer
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        archetypeName={playerData?.archetypes[0]?.archetypeName!}
        activeArchetypeID={playerData?.archetypes[0]?.id!}
        playerPosition={playerData?.positionTag[0]}
      />}
    </div>
  );
};

export default PlayerOverview;
